
import React, { useContext, useEffect, useState } from 'react';

import axios from 'axios';

import { AppContext } from '~/contexts/AppContext';
import { MenuScreen, ProfileData } from '~/App';

import styled from 'styled-components';
import StyledContentPanel from '~/components/StyledContentPanel';
import StyledButton from '~/components/StyledButton';
import StyledDropdown from '~/components/StyledDropdown';
import StyledTextInput from '~/components/StyledTextInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons/faArrowLeft';
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import Fade from '~/components/Fade';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { capitalizeFirst } from '~/util/util';

const ProfileDropDown = styled(StyledDropdown)`
    margin-bottom: 20px;
    background: #0006;
    padding: 21px 28px;
`;

const ProfileStyle = styled.div`
    padding: 20px;
`;

const ProfileDivider = styled.hr`
    margin: 40px 0;
    height: 3px;
    border: none;
    background-color: #00000055;
`;

interface ProfileFormData {
    locale: string;
    name: string;
    gender: 'male' | 'female' | 'other' | null;
    birthYear: number | null;
    cityName: string;
    regionName: string;
    countryName: string;
}

interface ProfileScreenState {
    formChanged: boolean;
    formData: ProfileFormData;
}

const ProfileComponent: React.FC = () => {
    const { t } = useTranslation();

    const { apiUrl, profile, setProfile, topicData, setMenuScreen } = useContext(AppContext)!;

    const initFormData = (profile: ProfileData | null) => ({
        locale: profile?.locale ?? '',
        name: profile?.name ?? '',
        gender: profile?.gender ?? null,
        birthYear: profile?.birthYear ?? null,
        cityName: profile?.cityName ?? '',
        regionName: profile?.regionName ?? '',
        countryName: profile?.countryName ?? ''
    });

    const [state, setState] = useState<ProfileScreenState>({
        formChanged: false,
        formData: initFormData(profile)
    });

    useEffect(() => {
        if (profile)
            setState({ ...state, formData: { ...state.formData, birthYear: profile.birthYear } });
    }, [profile?.birthYear]);

    useEffect(() => {
        if (profile)
            setState({ ...state, formData: { ...state.formData, gender: profile.gender } });
    }, [profile?.gender]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        setState({
            ...state,
            formChanged: true,
            formData: { ...state.formData, [e.target.name]: e.target.value }
        });
    };

    const saveChanges = async () => {
        const updateUserRequest: { user: Record<string, unknown>, topicId?: string } = { user: {} };

        if(!profile) return;

        for (const fieldKey of Object.keys(state.formData) as string[]) {
            const formField = state.formData[fieldKey as keyof typeof state.formData];
            const profileField = profile[fieldKey as keyof typeof profile];

            if (formField !== profileField && formField)
                updateUserRequest.user[fieldKey] = formField;
        }

        if (Object.keys(updateUserRequest.user).length > 0) {
            try {
                await axios.put(`${apiUrl}/user`, updateUserRequest);
                for (const fieldKey of Object.keys(state.formData)) {
                    (profile as any)[fieldKey as keyof typeof profile] = state.formData[fieldKey as keyof typeof state.formData];
                }
                setProfile({ ...profile });
                setState({ ...state, formChanged: false, });

                toast.success(t('menu_profile_toast_success'), {
                    style: {
                        borderRadius: '10px',
                        background: '#666',
                        color: '#fff',
                    },
                });
            } catch (err) {
                toast.error(t('general_error_message'), {
                    style: {
                        borderRadius: '10px',
                        background: '#666',
                        color: '#fff',
                    },
                });
            }
        }
    };

    const cancelChanges = () => {
        toast('Промените са отказани.', {
            style: {
                borderRadius: '10px',
                background: '#666',
                color: '#fff',
            },
        });
        setState({
            formChanged: false,
            formData: initFormData(profile)
        });
    };

    const genders = [
        { value: 'male', name: capitalizeFirst(t('general_male')) },
        { value: 'female', name: capitalizeFirst(t('general_female')) },
    ];

    const currentYear = new Date().getFullYear();
    const birthYears = Array.from({ length: 90 }, (_, i) => currentYear - i).map(year =>
        ({ value: year, name: year })
    );

    return (
        !profile ? null :
            <ProfileStyle>
                <Fade cascade>
                    <StyledContentPanel style={{ marginBottom: '20px' }}>
                        <h1>
                            <span style={{ marginRight: '10px' }}><FontAwesomeIcon icon={faUser} /></span>
                            {t('menu_profile_title')}
                        </h1>
                    </StyledContentPanel>
                    <StyledButton onClick={() => setMenuScreen(MenuScreen.MainMenu)}>
                        <span style={{ marginRight: '10px' }}><FontAwesomeIcon icon={faArrowLeft} /></span>
                        {t('button_back')}
                    </StyledButton>
                    <StyledContentPanel>
                        {/*<ProfileDropDown name='locale' onChange={handleChange}>
                            {locales.map(locale =>
                                <option value={locale.value} selected={state.formData.locale === locale.value}>
                                    {locale.name}
                                </option>
                            )}
                        </ProfileDropDown>*/}
                        <StyledTextInput type='text' placeholder={t('input_placeholder_name')} onChange={handleChange} name={'name'} value={state.formData.name} />
                        <ProfileDropDown name='gender' onChange={handleChange} value={state.formData.gender || ''}>
                            <option value="" disabled>{t('input_placeholder_gender')}</option>
                            {genders.map(gender =>
                                <option key={gender.value} value={gender.value}>
                                    {gender.name}
                                </option>
                            )}
                        </ProfileDropDown>
                        <ProfileDropDown name='birthYear' onChange={handleChange} value={state.formData.birthYear || ''}>
                            <option value="" disabled>{t('input_placeholder_birth_year')}</option>
                            {birthYears.map(birthYear =>
                                <option key={birthYear.value} value={birthYear.value}>
                                    {birthYear.name}
                                </option>
                            )}
                        </ProfileDropDown>
                        <StyledTextInput type='text' name='cityName' placeholder={t('input_placeholder_city')} onChange={handleChange} value={state.formData.cityName} />
                        <StyledTextInput type='text' name='regionName' placeholder={t('input_placeholder_region')} onChange={handleChange} value={state.formData.regionName} />
                        <StyledTextInput style={{ marginBottom: 0 }} type='text' name='countryName' placeholder={t('input_placeholder_country')} onChange={handleChange} value={state.formData.countryName} />
                    </StyledContentPanel>
                    <StyledButton style={{ marginTop: '20px' }} disabled={!state.formChanged} onClick={saveChanges}>
                        <span style={{ marginRight: '10px' }}><FontAwesomeIcon icon={faCheck} /></span>
                        {t('menu_profile_button_save')}
                    </StyledButton>
                    <StyledButton disabled={!state.formChanged} onClick={cancelChanges}>
                        <span style={{ marginRight: '10px' }}><FontAwesomeIcon icon={faXmark} /></span>
                        {t('menu_profile_button_discard')}
                    </StyledButton>
                    <ProfileDivider />
                    <StyledButton style={{ backgroundColor: '#ff0000b0', color: 'white' }} onClick={() => setMenuScreen(MenuScreen.DeleteProfileAYS)}>
                        {t('menu_profile_button_delete')}
                    </StyledButton>
                </Fade>
            </ProfileStyle>
    );
};

export default ProfileComponent;